/**
 * Panel-side state for the in-meeting assistant. Messages arrive over the
 * LiveKit text stream topic `octor.assistant` (sent only to AGIMA identities):
 *   snapshot   — full agenda state (items, hints, status, summary, timing)
 *   transcript — one recognised line
 *   status     — a note for the host (warn / error / info)
 * Pure functions, no React: see assistant-state.test.ts.
 */

export const ASSISTANT_TOPIC = 'octor.assistant';

export type AgendaItemStatus = 'pending' | 'in_progress' | 'done' | 'skipped';

export interface AgendaItem {
  id: string;
  kind: 'topic' | 'question';
  title: string;
  minutes: number | null; // planned minutes, from a prepared agenda
  priority: boolean; // part of the must-ask minimum
  doneWhen: string | null; // what has to be said for the item to count as closed
  speaker: string | null; // who presents the item (a rehearsal of a pitch)
  status: AgendaItemStatus;
  evidence: string | null;
  updatedAt: string | null;
}

export type AssistantHintKind = 'hint' | 'question' | 'speaker';

export interface AssistantHint {
  id: string;
  at: string;
  text: string;
  itemId: string | null;
  source: 'model' | 'timing';
  kind: AssistantHintKind; // question — the commission's question on a rehearsal; speaker — a swapped speaker
}

export type MeetingKind = 'meeting' | 'rehearsal';

export interface AssistantNote {
  id: string;
  at: string;
  level: 'info' | 'warn' | 'error';
  text: string;
}

export interface TranscriptLine {
  at: string;
  speaker: string | null;
  text: string;
}

/** Time per item as the assistant last reported it; the current one keeps ticking locally. */
export interface ItemTiming {
  id: string;
  plannedMin: number;
  spentMin: number;
  current: boolean;
}

export type AssistantRunStatus =
  | 'starting'
  | 'listening'
  | 'reconnecting'
  | 'stopped'
  | 'failed'
  | 'unknown';

export interface AssistantPanelState {
  kind: MeetingKind;
  connected: boolean; // at least one message received
  runStatus: AssistantRunStatus;
  version: number;
  items: AgendaItem[];
  hints: AssistantHint[]; // in the order they were given, oldest first
  notes: AssistantNote[]; // newest first, max 3
  transcript: TranscriptLine[]; // last few lines
  summary: { total: number; done: number; inProgress: number; skipped: number; pending: number };
  clockStartedAt: string | null; // when the conversation began (the first words)
  itemTiming: ItemTiming[]; // from the last snapshot
  shiftMin: number; // from the last snapshot: positive = behind the plan
  timingAt: string | null; // when itemTiming/shiftMin were measured
  lastMessageAt: string | null;
}

export const EMPTY_ASSISTANT_STATE: AssistantPanelState = {
  kind: 'meeting',
  connected: false,
  runStatus: 'unknown',
  version: 0,
  items: [],
  hints: [],
  notes: [],
  transcript: [],
  summary: { total: 0, done: 0, inProgress: 0, skipped: 0, pending: 0 },
  clockStartedAt: null,
  itemTiming: [],
  shiftMin: 0,
  timingAt: null,
  lastMessageAt: null,
};

const MAX_NOTES = 3;
const MAX_TRANSCRIPT = 6;
const STATUSES: AgendaItemStatus[] = ['pending', 'in_progress', 'done', 'skipped'];

export function parseAssistantMessage(raw: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && typeof parsed.type === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function asItems(value: unknown): AgendaItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item) => item && typeof item === 'object' && typeof (item as AgendaItem).id === 'string',
    )
    .map((item) => {
      const i = item as Partial<AgendaItem>;
      return {
        id: i.id as string,
        kind: i.kind === 'topic' ? 'topic' : 'question',
        title: String(i.title ?? ''),
        minutes: typeof i.minutes === 'number' && i.minutes > 0 ? i.minutes : null,
        priority: i.priority === true,
        doneWhen: typeof i.doneWhen === 'string' && i.doneWhen ? i.doneWhen : null,
        speaker: typeof i.speaker === 'string' && i.speaker ? i.speaker : null,
        status: STATUSES.includes(i.status as AgendaItemStatus)
          ? (i.status as AgendaItemStatus)
          : 'pending',
        evidence: typeof i.evidence === 'string' && i.evidence ? i.evidence : null,
        updatedAt: typeof i.updatedAt === 'string' ? i.updatedAt : null,
      };
    });
}

function asHints(value: unknown): AssistantHint[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (hint) =>
        hint && typeof hint === 'object' && typeof (hint as AssistantHint).text === 'string',
    )
    .map((hint, index) => {
      const h = hint as Partial<AssistantHint>;
      return {
        id: typeof h.id === 'string' ? h.id : `h-${index}`,
        at: typeof h.at === 'string' ? h.at : '',
        text: h.text as string,
        itemId: typeof h.itemId === 'string' ? h.itemId : null,
        source: h.source === 'timing' ? ('timing' as const) : ('model' as const),
        kind: h.kind === 'question' || h.kind === 'speaker' ? h.kind : ('hint' as const),
      };
    });
}

function asItemTiming(value: unknown): ItemTiming[] {
  if (!value || typeof value !== 'object') return [];
  const items = (value as { items?: unknown }).items;
  if (!Array.isArray(items)) return [];
  return items
    .filter((entry) => entry && typeof entry === 'object' && typeof (entry as ItemTiming).id === 'string')
    .map((entry) => {
      const t = entry as Partial<ItemTiming>;
      return {
        id: t.id as string,
        plannedMin: typeof t.plannedMin === 'number' ? t.plannedMin : 0,
        spentMin: typeof t.spentMin === 'number' ? t.spentMin : 0,
        current: t.current === true,
      };
    });
}

export function applyAssistantMessage(
  state: AssistantPanelState,
  message: Record<string, unknown>,
  now: () => string = () => new Date().toISOString(),
): AssistantPanelState {
  const at = now();
  switch (message.type) {
    case 'snapshot': {
      const version = typeof message.version === 'number' ? message.version : state.version;
      // Snapshots may arrive out of order after a reconnect; never go backwards.
      if (state.connected && version < state.version) return { ...state, lastMessageAt: at };
      const summary = (message.summary as AssistantPanelState['summary']) || state.summary;
      const timing = message.timing as { shiftMin?: unknown } | null | undefined;
      return {
        ...state,
        connected: true,
        version,
        runStatus: (message.status as AssistantRunStatus) || state.runStatus,
        kind: message.kind === 'rehearsal' ? 'rehearsal' : 'meeting',
        items: asItems(message.items),
        hints: asHints(message.hints),
        summary,
        clockStartedAt: timingStart(message.timing) ?? state.clockStartedAt,
        itemTiming: asItemTiming(message.timing),
        shiftMin: timing && typeof timing.shiftMin === 'number' ? timing.shiftMin : 0,
        timingAt: at,
        lastMessageAt: at,
      };
    }
    case 'transcript': {
      const line: TranscriptLine = {
        at: typeof message.at === 'string' ? message.at : at,
        speaker: typeof message.speaker === 'string' ? message.speaker : null,
        text: String(message.text ?? ''),
      };
      if (!line.text) return state;
      return {
        ...state,
        connected: true,
        transcript: [...state.transcript, line].slice(-MAX_TRANSCRIPT),
        lastMessageAt: at,
      };
    }
    case 'status': {
      const level = message.level === 'error' || message.level === 'warn' ? message.level : 'info';
      const note: AssistantNote = {
        id: `n-${at}-${state.notes.length}`,
        at,
        level,
        text: String(message.text ?? ''),
      };
      if (!note.text) return state;
      return {
        ...state,
        connected: true,
        notes: [note, ...state.notes].slice(0, MAX_NOTES),
        lastMessageAt: at,
      };
    }
    default:
      return state;
  }
}

function timingStart(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const startedAt = (value as { startedAt?: unknown }).startedAt;
  return typeof startedAt === 'string' && startedAt ? startedAt : null;
}

/** A prepared agenda has planned minutes: it is a checklist run in its own order. */
export function isTimedAgenda(items: AgendaItem[]): boolean {
  return items.some((item) => item.minutes);
}

export interface TimingView {
  started: boolean;
  elapsedMin: number;
  plannedMin: number;
  currentItemId: string | null;
  behindMin: number; // overrun of the current item against the plan
  overtime: boolean;
  shiftMin: number; // positive = behind the plan, negative = ahead
  items: Map<string, ItemTiming>; // spentMin of the current item ticks locally
}

/**
 * Same arithmetic as the assistant's timing check (assistant/src/state.js),
 * recomputed locally so the clock ticks between snapshots.
 */
export function timingView(state: AssistantPanelState, nowMs: number): TimingView | null {
  const plannedMin = state.items.reduce((sum, item) => sum + (item.minutes || 0), 0);
  if (!plannedMin) return null;
  const startedMs = state.clockStartedAt ? Date.parse(state.clockStartedAt) : NaN;
  const started = Number.isFinite(startedMs);
  const elapsedMin = started ? Math.max(0, (nowMs - startedMs) / 60000) : 0;
  // Same rule as currentItemIndex in assistant/src/state.js: the first open
  // item starting from the most recently discussed one.
  const isOpen = (item: AgendaItem) => item.status !== 'done' && item.status !== 'skipped';
  let last = -1;
  let lastAt = '';
  state.items.forEach((item, i) => {
    if (item.updatedAt && item.updatedAt >= lastAt) {
      last = i;
      lastAt = item.updatedAt;
    }
  });
  let index = -1;
  for (let i = Math.max(0, last); i < state.items.length && index < 0; i += 1)
    if (isOpen(state.items[i]!)) index = i;
  for (let i = 0; i < state.items.length && index < 0; i += 1)
    if (isOpen(state.items[i]!)) index = i;
  let currentEnd = 0;
  for (let i = 0; i <= index; i += 1) currentEnd += state.items[i]!.minutes || 0;
  const currentItemId = index >= 0 ? state.items[index]!.id : null;

  // Per-item time: the snapshot's numbers, plus the minutes that passed since
  // the snapshot for the item that was current then.
  const sinceSnapshotMin =
    started && state.timingAt ? Math.max(0, (nowMs - Date.parse(state.timingAt)) / 60000) : 0;
  const items = new Map<string, ItemTiming>();
  let shiftMin = state.shiftMin;
  for (const entry of state.itemTiming) {
    const spentMin = entry.current ? entry.spentMin + sinceSnapshotMin : entry.spentMin;
    if (entry.current && entry.plannedMin) {
      shiftMin += Math.max(0, spentMin - entry.plannedMin) - Math.max(0, entry.spentMin - entry.plannedMin);
    }
    items.set(entry.id, { ...entry, spentMin });
  }
  return {
    started,
    elapsedMin,
    plannedMin,
    currentItemId,
    behindMin: started && currentItemId ? Math.max(0, elapsedMin - currentEnd) : 0,
    overtime: started && elapsedMin > plannedMin,
    shiftMin: Math.round(shiftMin * 10) / 10,
    items,
  };
}

/** Items in a stable, host-friendly order: open work first, closed last. */
export function orderItems(items: AgendaItem[]): AgendaItem[] {
  if (isTimedAgenda(items)) return items;
  const rank: Record<AgendaItemStatus, number> = {
    in_progress: 0,
    pending: 1,
    done: 2,
    skipped: 3,
  };
  return [...items].sort(
    (a, b) => rank[a.status] - rank[b.status] || items.indexOf(a) - items.indexOf(b),
  );
}

export function statusLabel(status: AgendaItemStatus): string {
  switch (status) {
    case 'in_progress':
      return 'обсуждается';
    case 'done':
      return 'закрыто';
    case 'skipped':
      return 'отложено';
    default:
      return 'ещё не было';
  }
}

export function runStatusLabel(status: AssistantRunStatus, connected: boolean): string {
  if (!connected) return 'Помощник подключается…';
  switch (status) {
    case 'listening':
      return 'Слушает встречу';
    case 'starting':
      return 'Подключается…';
    case 'reconnecting':
      return 'Переподключается…';
    case 'stopped':
      return 'Остановлен';
    case 'failed':
      return 'Связь с моделью потеряна';
    default:
      return '';
  }
}

/** A hint is resolved once its agenda item is closed (done or skipped). */
export function isHintResolved(hint: AssistantHint, items: AgendaItem[]): boolean {
  if (!hint.itemId) return false;
  const item = items.find((candidate) => candidate.id === hint.itemId);
  return !!item && (item.status === 'done' || item.status === 'skipped');
}

/**
 * Hints next to the questions they are about: per agenda item, oldest first,
 * plus the general ones (no item or an unknown item) in the same order.
 */
export function groupHints(
  hints: AssistantHint[],
  items: AgendaItem[],
): {
  byItem: Map<string, AssistantHint[]>;
  general: AssistantHint[];
} {
  const known = new Set(items.map((item) => item.id));
  const byItem = new Map<string, AssistantHint[]>();
  const general: AssistantHint[] = [];
  for (const hint of hints) {
    if (hint.itemId && known.has(hint.itemId)) {
      const list = byItem.get(hint.itemId) ?? [];
      list.push(hint);
      byItem.set(hint.itemId, list);
    } else {
      general.push(hint);
    }
  }
  return { byItem, general };
}

/**
 * What the panel shows of a hint list by default: the newest one; the rest
 * are behind «ещё N». Keeps the panel to one line per item.
 */
export function visibleHints(hints: AssistantHint[], expanded: boolean): { shown: AssistantHint[]; hidden: number } {
  if (expanded || hints.length <= 1) return { shown: hints, hidden: 0 };
  return { shown: hints.slice(-1), hidden: hints.length - 1 };
}

export function newHints(previous: AssistantHint[], current: AssistantHint[]): AssistantHint[] {
  const seen = new Set(previous.map((hint) => hint.id));
  return current.filter((hint) => !seen.has(hint.id));
}
